import SlideshowIcon from '@mui/icons-material/Slideshow';
import { Avatar, Box, Button, Chip, Paper, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, TableSortLabel, Tooltip } from '@mui/material';
import { getLocaleDateWithTimeCustom, getUserFullName, highlightText, sortByProperty } from 'app/utils/appHelpers';
import config from 'config';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentMeeting } from 'stores/meeting';
import { useTemplateFontSettings } from 'styles/templateFontSettings';
import WatchingMeetingDataTableFilter from './WatchingMeetingDataTableFilter';
import WatchingMeetingDataTableToolbar from './WatchingMeetingDataTableToolbar';
import { useNavigate } from 'react-router-dom';
import { useJumboLayoutSidebar } from '@jumbo/hooks';
import { getObjectListByPropertyApi } from 'api/general';
import { MEETING_USER } from 'app/utils/constants/customConstant';


function WatchingMeetingDataTable({ mainData, total, column, pageTitle, onQueryChange }) {

    const dispatch = useDispatch();
    const { data: userList } = useSelector(state => state.user);

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("statFinish");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [appliedFilters, setAppliedFilters] = useState({});
    const unitList = useSelector(state => state.unit.data || []);
    const [data, setData] = useState(mainData || []);
    const navigate = useNavigate();
    const [highlightQuery, setHighlightQuery] = useState('');
    const lastQueryRef = useRef('');

    useEffect(() => {
        setData(mainData || []);
    }, [mainData]);

    useEffect(() => {
        if (!onQueryChange) return;
        const nextQuery = {
            page: rowsPerPage === -1 ? 0 : page,
            limit: rowsPerPage,
            q: searchQuery,
            sortBy: orderBy,
            sortOrder: order,
            filters: appliedFilters,
        };
        const key = JSON.stringify(nextQuery);
        if (lastQueryRef.current === key) return;
        lastQueryRef.current = key;
        onQueryChange(nextQuery);
    }, [page, rowsPerPage, searchQuery, order, orderBy, appliedFilters]);

    const { sidebarOptions, setSidebarOptions } = useJumboLayoutSidebar();

    const handleWatchingButtonClick = async (meeting) => {

        const resultMeetingMembers = await getObjectListByPropertyApi(MEETING_USER, "meetingId", meeting.id);
        let data = resultMeetingMembers.data;
        data = data.filter(x => x.type === 1)
        data = sortByProperty(data, "indexNo");
        let mUsers = []

        data.forEach(element => {
            const user = userList.find(item => item.id === element.userId);
            mUsers.push(user)
        });

        const updatedMeeting = {
            ...meeting,
            users: mUsers,
        };
        dispatch(setCurrentMeeting(updatedMeeting));

        navigate('/previousPage/watching');
        setSidebarOptions({ open: false })
    }

    const handleRequestSort = (event, property) => {
        if (!['id', 'inst', 'unit', 'meetingNo', 'pdate', 'adminUserId', 'statFinish'].includes(property)) {
            return;
        }
        const tempOrderBy = property;
        let tempOrder = 'desc';

        if (orderBy === property && order === 'desc') {
            tempOrder = 'asc';
        }

        setOrder(tempOrder);
        setOrderBy(tempOrderBy);
        setPage(0);
    };

    const DataTableHead = ({ onRequestSort, customColumnData }) => {

        const createSortHandler = property => event => {
            onRequestSort(event, property);
        };

        return (
            <TableHead>
                <TableRow>
                    {customColumnData.map(column => {
                        return (
                            <TableCell
                                key={column.id}
                                align={column.numeric}
                                padding={column.disablePadding ? 'none' : 'normal'}
                                sx={{ whiteSpace: 'normal' }}
                            >
                                <Tooltip
                                    title="Sırala"
                                    placement={column.numeric ? 'bottom-end' : 'bottom-start'}
                                    enterDelay={300}
                                >
                                    <TableSortLabel
                                        active={orderBy === column.id}
                                        direction={order}
                                        onClick={createSortHandler(column.id)}
                                        sx={{ whiteSpace: 'normal', '& .MuiTableSortLabel-icon': { flexShrink: 0 } }}
                                    >
                                        {column.label}
                                    </TableSortLabel>
                                </Tooltip>
                            </TableCell>
                        );
                    }, this)}
                </TableRow>
            </TableHead>
        );
    };




    const { baseFontSize, fabStyle, paginationStyle, buttonStyle, iconFontSize, tableCellStyle } = useTemplateFontSettings();


    return (

        <Paper className="col-md-12" sx={{ minWidth: 0, overflow: 'hidden' }}>
            <WatchingMeetingDataTableToolbar
                pageTitle={pageTitle}
                setHighlightQuery={setHighlightQuery}
                onSearchSubmit={(q) => {
                    setSearchQuery(q);
                    setPage(0);
                }}
            />
            <WatchingMeetingDataTableFilter
                onApplyFilters={(filters) => {
                    setAppliedFilters(filters);
                    setPage(0);
                }}
                onClearFilters={() => {
                    setAppliedFilters({});
                    setPage(0);
                }}
            />
            <div className="flex-auto" style={{ minWidth: 0 }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table
                        sx={{
                            minWidth: 960,
                            '& th, & td': {
                                fontSize: baseFontSize
                            },
                            '& .MuiTableCell-head': { whiteSpace: 'normal' },
                            '& .MuiTableSortLabel-root': { whiteSpace: 'normal' },
                            '& .MuiTableSortLabel-icon': { flexShrink: 0 },
                        }}
                        size="small"
                    >
                        <DataTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={data.length}
                            customColumnData={column}
                        />

                        <TableBody>

                            {data !== undefined && data.length === 0 ? null : (
                                data.map(n => {
                                    const user = userList.find(x => x.id === n.adminUserId);
                                    return (
                                        <TableRow hover key={"trO" + n.id} >
                                            <TableCell align="left" padding="normal">{highlightText(n.inst, highlightQuery)}</TableCell>
                                            <TableCell align="left" padding="normal">{highlightText(unitList.find(x => x.id === n.unitId)?.name || "Tanımsız Kurul/Kol/Komisyon", highlightQuery)}</TableCell>
                                            <TableCell align="left" padding="normal" sx={tableCellStyle}>{highlightText(`${n.meetingNo}`, highlightQuery)}</TableCell>
                                            <TableCell align="left" padding="normal" sx={tableCellStyle}>{getLocaleDateWithTimeCustom("tr", n.pdate)}</TableCell>
                                            <TableCell align="left" padding="none">
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar
                                                        style={{
                                                            ...fabStyle
                                                        }}
                                                        alt={user !== undefined ? user.name : ''}
                                                        src={
                                                            user && user.profileImageUrl
                                                                ? `${config.userAuthApiUrl}${user.profileImageUrl}`
                                                                : undefined
                                                        }
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src =
                                                                config.userAuthApiUrl + config.defaultProfileImageUrl;
                                                        }}
                                                    />
                                                    <span>{highlightText(getUserFullName(user), highlightQuery)}</span>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" padding="none">
                                                {n.statFinish === 1 ? (
                                                    <Chip
                                                        label="Tamamlandı"
                                                        size="small"
                                                        color="success"
                                                        sx={{
                                                            mb: 1,
                                                            mt: 1,
                                                            height: '20px',
                                                            '& .MuiChip-label': {
                                                                fontSize: '10px',
                                                                paddingLeft: '4px',
                                                                paddingRight: '4px',
                                                            },
                                                        }}
                                                    />
                                                ) : (n.statVote === 1 || n.statReview === 1 || n.statVideo === 1) ? (
                                                    <Chip
                                                        label="İşleme Açık"
                                                        size="small"
                                                        color="info"
                                                        sx={{
                                                            mb: 1,
                                                            mt: 1,
                                                            height: '20px',
                                                            '& .MuiChip-label': {
                                                                fontSize: '10px',
                                                                paddingLeft: '4px',
                                                                paddingRight: '4px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Tamamlanmadı"
                                                        size="small"
                                                        color="warning"
                                                        sx={{
                                                            mb: 1,
                                                            mt: 1,
                                                            height: '20px',
                                                            '& .MuiChip-label': {
                                                                fontSize: '10px',
                                                                paddingLeft: '4px',
                                                                paddingRight: '4px',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center" padding="none">
                                                <Tooltip title="Toplantı İzle">
                                                    <Box component="span" sx={{ display: 'inline-flex' }}>
                                                        <Button
                                                            sx={buttonStyle}
                                                            onClick={async () => {
                                                                await handleWatchingButtonClick(n);
                                                            }}
                                                        >
                                                            <SlideshowIcon />
                                                        </Button>
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                            {data !== undefined && data.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Toplantı Bulunamadı
                                    </TableCell>
                                </TableRow>
                            )}



                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    count={total}
                                    rowsPerPage={rowsPerPage}
                                    page={rowsPerPage === -1 ? 0 : page}
                                    onPageChange={(e, nextPage) => setPage(nextPage)}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(Number(e.target.value));
                                        setPage(0);
                                    }}
                                    labelRowsPerPage={"Görüntülenecek Satır"}
                                    rowsPerPageOptions={[10, 50, 100, { label: "Tümü", value: -1 }]}
                                    showFirstButton
                                    showLastButton
                                    sx={paginationStyle}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </Box>
            </div>




        </Paper >
    )
}

export default WatchingMeetingDataTable